import React from "react";
import { Youtube, Instagram, Facebook, Linkedin, Music2, Send } from "lucide-react";
import { useSettings } from "../lib/settings";

const ICONS = {
  instagram: { Icon: Instagram, label: "Instagram", color: "#E1306C" },
  youtube: { Icon: Youtube, label: "YouTube", color: "#FF0000" },
  facebook: { Icon: Facebook, label: "Facebook", color: "#1877F2" },
  linkedin: { Icon: Linkedin, label: "LinkedIn", color: "#0A66C2" },
  tiktok: { Icon: TikTokIcon, label: "TikTok", color: "#fff" },
  spotify: { Icon: SpotifyIcon, label: "Spotify Podcast", color: "#1DB954" },
  telegram: { Icon: Send, label: "Telegram", color: "#26A5E4" },
  whatsapp: { Icon: WhatsAppIcon, label: "WhatsApp", color: "#25D366" },
};

const ORDER = ["instagram", "youtube", "facebook", "linkedin", "tiktok", "spotify", "telegram", "whatsapp"];

function TikTokIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.45a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.06z"/></svg>
  );
}

function SpotifyIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.42 1.56-.299.421-1.02.599-1.559.3z"/></svg>
  );
}

function WhatsAppIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M.057 24l1.687-6.163a11.867 11.867 0 0 1-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 0 1 8.413 3.488 11.824 11.824 0 0 1 3.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 0 1-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.71.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413z"/></svg>
  );
}

/**
 * Renders the configured social icons.
 * variant: "header" (small, dim) | "footer" (medium) | "cta" (large, gold border, hover bring-to-life)
 */
export default function SocialIcons({ variant = "footer", className = "" }) {
  const settings = useSettings();
  const social = settings?.social || {};

  const items = ORDER
    .map((key) => ({ key, url: social[key], ...ICONS[key] }))
    .filter((it) => !!it.url && it.url.trim() !== "");

  if (items.length === 0 && variant !== "cta") return null;

  if (variant === "header") {
    return (
      <div className={`flex items-center gap-1.5 ${className}`}>
        {items.map(({ key, url, Icon, label }) => (
          <a key={key} href={url} target="_blank" rel="noopener noreferrer" aria-label={label}
             className="w-8 h-8 flex items-center justify-center rounded-full text-white/55 hover:text-[#D4AF37] hover:bg-white/5 transition"
             data-testid={`social-${key}`}>
            <Icon size={14} />
          </a>
        ))}
      </div>
    );
  }

  if (variant === "cta") {
    // Nur Icons mit gültiger URL rendern — kein href="#" mehr (Block 1 · Mastermatrix)
    if (items.length === 0) return null;
    return (
      <div className={`flex flex-wrap items-center justify-center gap-3 ${className}`}>
        {items.map(({ key, url, Icon, label, color }) => (
          <a key={key} href={url} target="_blank" rel="noopener noreferrer" aria-label={label}
             className="group relative w-14 h-14 flex items-center justify-center rounded-full border border-white/15 hover:border-[#D4AF37] transition-transform hover:scale-110 hover:-translate-y-1"
             style={{ background: "rgba(255,255,255,0.02)" }}
             data-testid={`social-cta-${key}`}>
            <Icon size={20} />
            <span className="pointer-events-none absolute -bottom-7 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition text-[10px] uppercase tracking-wider whitespace-nowrap text-white/65">{label}</span>
            <span className="pointer-events-none absolute inset-0 rounded-full opacity-0 group-hover:opacity-30 transition" style={{ background: `radial-gradient(circle, ${color} 0%, transparent 70%)` }} />
          </a>
        ))}
      </div>
    );
  }

  // footer (default)
  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`}>
      {items.map(({ key, url, Icon, label }) => (
        <a key={key} href={url} target="_blank" rel="noopener noreferrer" aria-label={label}
           className="w-9 h-9 flex items-center justify-center rounded-full border border-white/10 text-white/65 hover:text-[#D4AF37] hover:border-[#D4AF37] transition"
           data-testid={`social-${key}`}>
          <Icon size={15} />
        </a>
      ))}
    </div>
  );
}
